import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { KeyOfType } from '@ipfabric/utils/types';
import { produce } from 'immer';
import {
  type CSSProperties,
  type ReactNode,
  forwardRef,
  memo,
  useState,
} from 'react';
import Scrollbars, { type ScrollbarProps } from 'react-custom-scrollbars-2';

export type ScrollViewProps = ScrollbarProps & {
  /**
   * Provide extra class name to the inner view element for default content view
   */
  contentClassName?: string;
  styleCorrections?: StyleCorrections;
};

export interface StyleCorrections {
  marginRight?: number;
  marginBottom?: number;
}

interface ViewAttributes {
  style: CSSProperties;
}

const BareScrollView = forwardRef<Scrollbars, ScrollViewProps>(
  (
    {
      autoHide,
      autoHideTimeout = 10,
      children,
      contentClassName,
      styleCorrections,
      ...otherProps
    },
    ref,
  ) => {
    const [mouseOnContent, setMouseOnContent] = useState<boolean>(true);

    const handleMouseEnter = () => {
      setMouseOnContent(true);
    };

    const handleMouseLeave = () => {
      setMouseOnContent(false);
    };

    const applyStyleCorrections = (attrs: ViewAttributes) =>
      produce(attrs, (draft) => {
        const applyNumericCorrection = (
          property: KeyOfType<StyleCorrections, number | undefined>,
        ) => {
          const correction = styleCorrections?.[property];
          if (correction) {
            const currentValue = draft.style[property] ?? 0;
            if (typeof currentValue === 'number') {
              draft.style[property] = currentValue + correction;
            } else {
              stumble(
                `ScrollView: style.${property} is not a number, skipping CSS correction`,
              );
            }
          }
        };

        applyNumericCorrection('marginRight');
        applyNumericCorrection('marginBottom');
      });

    const getRenderView =
      () =>
      (attrs: ViewAttributes): ReactNode => (
        <div
          className={contentClassName}
          {...(styleCorrections ? applyStyleCorrections(attrs) : attrs)}
        />
      );
    return (
      <Scrollbars
        renderView={getRenderView()}
        {...otherProps}
        ref={ref}
        autoHide={autoHide ?? !mouseOnContent}
        autoHideTimeout={autoHideTimeout}
        hideTracksWhenNotNeeded
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </Scrollbars>
    );
  },
);
BareScrollView.displayName = 'ScrollView';

/**
 * ScrollView bundles autohiding scrollbar functionality and some fixes of default Scrollbars behavior,
 * otherwise it's API is same as Scrollbars
 *
 * Scrollbars component renders two nested divs - outer one with
 * `position: relative; overflow: hidden; width: 100%; height: 100%;`
 * and inner one wrapping the content.
 *
 * Our wrapper adds some style fixes of Scrollbars via custom renderView function.
 * We recommend to customize the style using `className` and `contentClassName` to reuse
 * these fixes the simplest way possible.
 *
 * @see {@link ScrollColumnView} for common usage in flex layouts
 */
export const ScrollView = memo(BareScrollView);

ScrollView.displayName = 'ScrollView';
